import { DocumentModel } from 'shared/domain/document/documentModel';
import { SyncStatus } from 'shared/domain/entitySyncStatus/syncStatus';
import { uploadFile } from 'serviceWorker/repository/file/postFile';
import { UploadStatus } from 'shared/types/uploadStatus';
import { setDocumentDrawingUploadStatus } from './setDocumentDrawingUploadStatus';
import { confirmUpload, failUpload } from './uploadConfirm';
import { IDocumentUploader } from './uploadDocument';
import { MIME_TYPES } from 'shared/constants/MIME_TYPES';
import { revokeBlobUrlsSafely } from 'shared/utils/blobUrl';

interface DrawingData {
  isDrawn: boolean;
  drawingSrc: string;
  mergedSrc?: string;
  fileType?: string;
  drawings?: Array<{ drawnUploadStatus?: UploadStatus | string; mergedUploadStatus?: UploadStatus | string }>;
}

export type UploadDrawing = (
    uploader: IDocumentUploader,
    document: DocumentModel,
    url: string,
    data: DrawingData
  ) => Promise<boolean>;

function buildDrawingPutBody(data: DrawingData, document: DocumentModel): Record<string, unknown> {
  const fileType = data.fileType ?? document.type ?? MIME_TYPES.OCTET_STREAM;
  const drawings = data.isDrawn
    ? (data.drawings ?? [{ drawnUploadStatus: UploadStatus.unknown, mergedUploadStatus: UploadStatus.unknown }])
    : [];
  return { isDrawn: data.isDrawn, fileType, drawings };
}

export const uploadDrawing: UploadDrawing = async function _uploadDrawing(
  uploader: IDocumentUploader,
  document: DocumentModel,
  url: string,
  data: DrawingData
): Promise<boolean> {
  const uploadToTarget = async (
    target: { fields: any; url: string },
    file: File,
    label: string
  ): Promise<void> => {
    const response = await uploadFile(
      new AbortController(),
      uploader.POST_FILE,
      target.fields,
      target.url,
      file
    );
    const status = typeof response?.status === 'number' ? response.status : undefined;
    if (response && 'ok' in response && !response.ok) {
      throw new Error(`uploadDrawing ${label} upload failed with status ${status}`);
    }
  };
  const putBody = buildDrawingPutBody(data, document);
  const signedData = await uploader.PUT(url, putBody);

  const drawingResponse: Response = await fetch(data.drawingSrc);
  const drawingBlob: Blob = await drawingResponse.blob();
  const fallbackFileType =
    document.type?.trim() || MIME_TYPES.OCTET_STREAM;
  const drawingFile = new File([drawingBlob], `drawing_${document._id}`, {
    type: drawingBlob.type || fallbackFileType,
  });
  const baseSignedRequest = signedData?.signedRequest;
  const drawingSignedRequest = signedData?.drawingSignedRequest;
  const mergedSignedRequest = signedData?.mergedSignedRequest;
  const hasMergedTarget = Boolean(mergedSignedRequest);
  const shouldUploadMergedToBase =
    Boolean(baseSignedRequest) &&
    !hasMergedTarget;
  let mergedFile: File | undefined;
  const getMergedFile = async (): Promise<File> => {
    if (mergedFile) {
      return mergedFile;
    }
    const mergedSource = data.mergedSrc || data.drawingSrc;
    const mergedResponse: Response = await fetch(mergedSource);
    const mergedBlob: Blob = await mergedResponse.blob();
    mergedFile = new File([mergedBlob], `merged_${document._id}`, {
      type: mergedBlob.type || fallbackFileType,
    });
    return mergedFile;
  };
  try {
    let uploadedMerged = false;
    let uploadedDrawing = false;

    if (!baseSignedRequest && !drawingSignedRequest && !mergedSignedRequest) {
      throw new Error('uploadDrawing: no upload targets in drawingStart response');
    }

    if (mergedSignedRequest) {
      const mergedFileToUpload = await getMergedFile();
      await uploadToTarget(mergedSignedRequest, mergedFileToUpload, 'mergedSignedRequest');
      uploadedMerged = true;
    } else if (shouldUploadMergedToBase && baseSignedRequest) {
      const mergedFileToUpload = await getMergedFile();
      await uploadToTarget(baseSignedRequest, mergedFileToUpload, 'baseSignedRequest(merged)');
      uploadedMerged = true;
    }
    if (drawingSignedRequest) {
      await uploadToTarget(drawingSignedRequest, drawingFile, 'drawingSignedRequest');
      uploadedDrawing = true;
    }

    if (!uploadedMerged && !uploadedDrawing) {
      throw new Error('uploadDrawing: no files uploaded before confirmation');
    }

    await confirmUpload(uploader, url);
    await setDocumentDrawingUploadStatus(
      document.localId,
      UploadStatus.success,
      SyncStatus.SUCCESS
    );
    // Keep mergedSrc alive until UI switches to mergedSignedRequest from getSources.
    revokeBlobUrlsSafely([data.drawingSrc]);
    return true;
  } catch (e) {
    await failUpload(uploader, url);
    await setDocumentDrawingUploadStatus(
      document.localId,
      UploadStatus.failed,
      SyncStatus.FAIL
    );
    return false;
  }
};
